/** @file Root component: session check with retry, route guard, <html lang>, and the error-bounded screen. */
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useStore } from 'zustand';
import { api } from '../api/api';
import { routeStore } from '../stores/routeStore';
import { sessionStore } from '../stores/sessionStore';
import { settingsStore } from '../stores/settingsStore';
import { ErrorFallback } from './ErrorFallback';
import { errorText } from './i18n/errorText';
import { useT } from './i18n/useT';
import { guardRoute } from './router/guardRoute';
import { navigate } from './router/navigate';
import { Screen } from './Screen';
import { checkSession } from './session/checkSession';

/** Splash until GET /auth/me answers (an unreachable Worker shows the error with a retry) and while the guard redirects, then the route. */
export function App() {
  const t = useT();
  const route = useStore(routeStore, (s) => s.route);
  const status = useStore(sessionStore, (s) => s.status);
  const lang = useStore(settingsStore, (s) => s.lang);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const boot = useCallback(() => {
    setFailure(null);
    checkSession(api).catch((thrown: unknown) => { setFailure({ thrown }); });
  }, []);
  const target = guardRoute(route, status);
  useEffect(boot, [boot]);
  useEffect(() => {
    if (target !== null) navigate(target, { replace: true });
  }, [target]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  if (status === 'unknown') {
    if (failure === null) return <p className="shell-splash">{t('shell.loading')}</p>;
    return (
      <div role="alert" className="shell-card shell-error">
        <p>{errorText(t, failure.thrown)}</p>
        <button type="button" onClick={boot}>{t('shell.retry')}</button>
      </div>
    );
  }
  if (target !== null) return <p className="shell-splash">{t('shell.loading')}</p>;
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[route]}>
      <Screen route={route} />
    </ErrorBoundary>
  );
}
